import React, { useState, useEffect } from 'react';

const SearchInterface = ({ onSearch, loading }) => {
  const [filters, setFilters] = useState({
    states: [],
    cities: [],
    zipCodes: [],
    services: [],
    specializations: [],
    panelBrands: [],
    inverterBrands: [],
    companySizes: [],
    activityLevels: [],
    employeeCountMin: '',
    employeeCountMax: '',
    minInstallations: '',
    maxInstallations: '',
    minQualityScore: '',
    verifiedSince: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    states: [],
    services: [],
    specializations: [],
    panelBrands: [],
    inverterBrands: [],
    companySizes: [],
    activityLevels: []
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch('/api/installers/filters');
      
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleMultiSelectChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: prev[filterName].includes(value)
        ? prev[filterName].filter(item => item !== value)
        : [...prev[filterName], value]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(filters);
  };

  const clearFilters = () => {
    setFilters({
      states: [],
      cities: [],
      zipCodes: [],
      services: [],
      specializations: [],
      panelBrands: [],
      inverterBrands: [],
      companySizes: [],
      activityLevels: [],
      employeeCountMin: '',
      employeeCountMax: '',
      minInstallations: '',
      maxInstallations: '',
      minQualityScore: '',
      verifiedSince: ''
    });
  };

  const styles = {
    container: {
      backgroundColor: 'rgba(13, 27, 49, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '2rem',
      marginBottom: '2rem',
      backdropFilter: 'blur(20px)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#FFFFFF',
      margin: 0
    },
    toggleButton: {
      backgroundColor: 'transparent',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#B0C4DE',
      padding: '0.5rem 1rem',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    form: {
      display: 'grid',
      gap: '1.5rem'
    },
    filterSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#B0C4DE',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    select: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '0.75rem',
      color: '#FFFFFF',
      fontSize: '0.9rem',
      minHeight: '45px'
    },
    input: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '0.75rem',
      color: '#FFFFFF',
      fontSize: '0.9rem'
    },
    multiSelect: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '0.5rem',
      minHeight: '100px',
      maxHeight: '150px',
      overflowY: 'auto'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem',
      cursor: 'pointer',
      fontSize: '0.9rem',
      color: '#FFFFFF'
    },
    checkboxInput: {
      width: '16px',
      height: '16px'
    },
    rangeGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.5rem'
    },
    actions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
      marginTop: '1rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none'
    },
    primaryButton: {
      backgroundColor: '#2979FF',
      color: '#FFFFFF',
      boxShadow: '0 4px 15px rgba(41, 121, 255, 0.3)'
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: '#B0C4DE',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    selectedCount: {
      fontSize: '0.8rem',
      color: '#00E676',
      marginTop: '0.25rem'
    }
  };

  const getSelectedCount = (filterName) => {
    const count = filters[filterName]?.length || 0;
    return count > 0 ? `${count} selected` : '';
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Search & Filter Installers</h2>
        <button
          style={styles.toggleButton}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
        </button>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Filters */}
        <div style={styles.filterSection}>
          {/* Location */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>States</label>
            <div style={styles.multiSelect}>
              {filterOptions.states.map(state => (
                <div key={state} style={styles.checkbox}>
                  <input
                    type="checkbox"
                    style={styles.checkboxInput}
                    checked={filters.states.includes(state)}
                    onChange={() => handleMultiSelectChange('states', state)}
                  />
                  <span>{state}</span>
                </div>
              ))}
            </div>
            <div style={styles.selectedCount}>{getSelectedCount('states')}</div>
          </div>

          {/* Services */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>Services Offered</label>
            <div style={styles.multiSelect}>
              {filterOptions.services.map(service => (
                <div key={service} style={styles.checkbox}>
                  <input
                    type="checkbox"
                    style={styles.checkboxInput}
                    checked={filters.services.includes(service)}
                    onChange={() => handleMultiSelectChange('services', service)}
                  />
                  <span>{service}</span>
                </div>
              ))}
            </div>
            <div style={styles.selectedCount}>{getSelectedCount('services')}</div>
          </div>

          {/* Company Size */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>Company Size</label>
            <div style={styles.multiSelect}>
              {filterOptions.companySizes.map(size => (
                <div key={size} style={styles.checkbox}>
                  <input
                    type="checkbox"
                    style={styles.checkboxInput}
                    checked={filters.companySizes.includes(size)}
                    onChange={() => handleMultiSelectChange('companySizes', size)}
                  />
                  <span style={{ textTransform: 'capitalize' }}>{size}</span>
                </div>
              ))}
            </div>
            <div style={styles.selectedCount}>{getSelectedCount('companySizes')}</div>
          </div>

          {/* Activity Level */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>Activity Level</label>
            <div style={styles.multiSelect}>
              {filterOptions.activityLevels.map(level => (
                <div key={level} style={styles.checkbox}>
                  <input
                    type="checkbox"
                    style={styles.checkboxInput}
                    checked={filters.activityLevels.includes(level)}
                    onChange={() => handleMultiSelectChange('activityLevels', level)}
                  />
                  <span style={{ textTransform: 'capitalize' }}>{level}</span>
                </div>
              ))}
            </div>
            <div style={styles.selectedCount}>{getSelectedCount('activityLevels')}</div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div style={styles.filterSection}>
            {/* Employee Count Range */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Number of Employees</label>
              <div style={styles.rangeGroup}>
                <input
                  type="number"
                  placeholder="Min"
                  style={styles.input}
                  value={filters.employeeCountMin}
                  onChange={(e) => handleFilterChange('employeeCountMin', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Max"
                  style={styles.input}
                  value={filters.employeeCountMax}
                  onChange={(e) => handleFilterChange('employeeCountMax', e.target.value)}
                />
              </div>
            </div>

            {/* Installation Count Range */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Total Installations</label>
              <div style={styles.rangeGroup}>
                <input
                  type="number"
                  placeholder="Min"
                  style={styles.input}
                  value={filters.minInstallations}
                  onChange={(e) => handleFilterChange('minInstallations', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Max"
                  style={styles.input}
                  value={filters.maxInstallations}
                  onChange={(e) => handleFilterChange('maxInstallations', e.target.value)}
                />
              </div>
            </div>

            {/* Quality Score */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Minimum Quality Score</label>
              <input
                type="number"
                placeholder="e.g. 80"
                min="0"
                max="100"
                style={styles.input}
                value={filters.minQualityScore}
                onChange={(e) => handleFilterChange('minQualityScore', e.target.value)}
              />
            </div>

            {/* Recently Verified */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Recently Verified</label>
              <select
                style={styles.select}
                value={filters.verifiedSince}
                onChange={(e) => handleFilterChange('verifiedSince', e.target.value)}
              >
                <option value="">All Time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            {/* Panel Brands */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Panel Brands</label>
              <div style={styles.multiSelect}>
                {filterOptions.panelBrands.map(brand => (
                  <div key={brand} style={styles.checkbox}>
                    <input
                      type="checkbox"
                      style={styles.checkboxInput}
                      checked={filters.panelBrands.includes(brand)}
                      onChange={() => handleMultiSelectChange('panelBrands', brand)}
                    />
                    <span>{brand}</span>
                  </div>
                ))}
              </div>
              <div style={styles.selectedCount}>{getSelectedCount('panelBrands')}</div>
            </div>

            {/* Inverter Brands */}
            <div style={styles.filterGroup}>
              <label style={styles.label}>Inverter Brands</label>
              <div style={styles.multiSelect}>
                {filterOptions.inverterBrands.map(brand => (
                  <div key={brand} style={styles.checkbox}>
                    <input
                      type="checkbox"
                      style={styles.checkboxInput}
                      checked={filters.inverterBrands.includes(brand)}
                      onChange={() => handleMultiSelectChange('inverterBrands', brand)}
                    />
                    <span>{brand}</span>
                  </div>
                ))}
              </div>
              <div style={styles.selectedCount}>{getSelectedCount('inverterBrands')}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={clearFilters}
          >
            Clear All Filters
          </button>
          <button
            type="submit"
            style={{ ...styles.button, ...styles.primaryButton }}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search Installers'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchInterface; 